/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("HVBoxSplitterWidget", ["SplitterWidget", "WidgetFactory"], function (context, cls) {
  /**
   * Splitter widget.
   * @class HVBoxSplitterWidget
   * @memberOf classes
   * @extends classes.SplitterWidget
   */
  cls.HVBoxSplitterWidget = context.oo.Class(cls.SplitterWidget, function ($super) {
    return /** @lends classes.HVBoxSplitterWidget.prototype */ {
      __name: "HVBoxSplitterWidget",
      __templateName: "SplitterWidget",
      /**
       * @description splitting orientation
       * @type {string}
       */
      _orientation: "vertical",
      /**
       * @type {boolean}
       * @description by default splitter will relayout whole page in realtime on dragging.
       * If deferred mode is on, we won't relayout but draw a feedback rectangle instead to add a lighter visual feedback
       */
      _deferredMode: false,
      /**
       * @type {HTMLElement}
       * @description feedback rectangle drawn on split when using light mode
       */
      _feedbackRect: null,
      /**
       * @type {Number}
       * @description half of the size of the splitter
       */
      _splitterSize: 0,
      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this.addClasses("gbc_SplitterWidget");
        this._deferredMode = context.ThemeService.getValue("gbc-SplitterWidget-performanceMode") === "deferred";
        this._splitterSize = Number.parseInt(context.ThemeService.getValue("gbc-SplitterWidget-splitter-size"), 10);
      },
      /**
       * @inheritDoc
       */
      _initLayout: function () {
        $super._initLayout.call(this);
        this._layoutInformation.setMaximal(cls.Size.maximal, 8);
      },
      /**
       * Set split orientation
       * @param orientation
       */
      setOrientation(orientation) {
        this._orientation = orientation;
        this.toggleClass("gbc_VBoxSplitterWidget", "gbc_HBoxSplitterWidget", this._isVertical());
        this._updateAriaValues();
      },
      /**
       * Update splitter position on drag/touchmove
       * @param delta
       */
      updateSplits: function (delta) {
        // A keyboard step is atomic — skip the deferred-mode drag feedback rectangle (GBC-5755).
        if (this._deferredMode && !this._keyboardMoving) {
          this._moveSplitter(delta);
        }
        // Only for HBOX
        if (!this._isVertical()) {
          if (this.isReversed()) {
            delta = -delta;
          }
        }
        $super.updateSplits.call(this, delta);
      },

      /**
       * A vertical bar (HBox, left/right panes) moves on the horizontal axis -> aria-orientation "vertical";
       * a horizontal bar (VBox, top/bottom panes) -> "horizontal". Must be explicit (separator default differs).
       * @inheritDoc
       */
      _getAriaOrientation: function () {
        return this._isVertical() ? "horizontal" : "vertical";
      },

      /**
       * @returns {number} this splitter's preceding-pane index, as used by the layout engine's startSplitting
       * @private
       */
      _paneIndex: function () {
        const box = this.getParentWidget();
        return box ? (box.getIndexOfChild(this) - 1) / 2 : -1;
      },

      /**
       * @returns {?{before:number, after:number}} allocated sizes of the two surrounding panes
       * @private
       */
      _paneSizes: function () {
        const engine = this.getParentWidget()?.getLayoutEngine?.();
        return engine?.getAllocatedSizesAround ? engine.getAllocatedSizesAround(this._paneIndex()) : null;
      },

      /**
       * @inheritDoc
       */
      _computeValueNow: function () {
        const sizes = this._paneSizes();
        if (!sizes) {
          return null;
        }
        const total = sizes.before + sizes.after;
        if (total <= 0) {
          return null;
        }
        return Math.round((sizes.before / total) * 100);
      },

      /**
       * @inheritDoc
       */
      _getStepDelta: function () {
        const sizes = this._paneSizes();
        return sizes ? Math.max(1, Math.round(0.05 * (sizes.before + sizes.after))) : 0;
      },

      /**
       * @inheritDoc
       */
      _getFullRangePx: function () {
        const sizes = this._paneSizes();
        return sizes ? sizes.before + sizes.after : 0;
      },

      /**
       * @inheritDoc
       */
      _primarySizePx: function () {
        const sizes = this._paneSizes();
        return sizes ? sizes.before : 0;
      },

      /**
       * `updateSplits` flips the sign for a reversed HBox (VBox never flips), so pre-compensate to keep a
       * positive primary delta always growing the "before" pane.
       * @inheritDoc
       */
      _screenDeltaForPrimaryGrow: function (primaryDelta) {
        if (this._isVertical()) {
          return primaryDelta;
        }
        return this.isReversed() ? -primaryDelta : primaryDelta;
      },

      /**
       * The splitter controls the primary pane — the box child just before it (W3C APG aria-controls).
       * @inheritDoc
       */
      _getAriaControlsId: function () {
        const box = this.getParentWidget();
        if (!box) {
          return null;
        }
        const rawIndex = box.getIndexOfChild(this); // raw children index of this splitter
        const primaryPane = rawIndex > 0 ? box.getChildren()[rawIndex - 1] : null;
        return primaryPane?.getRootClassName?.() || null;
      },
      /**
       * Returns true if splitter is in deferred mode (no relayout during split/drag)
       * @return {boolean}
       */
      isDeferred: function () {
        return this._deferredMode;
      },
      /**
       * Return true if splitter orientation is vertical
       * @return {boolean}
       * @private
       */
      _isVertical() {
        return this._orientation === "vertical";
      },
      /**
       * Handler called one time when starting a drag move on the splitter
       * @param evt
       * @private
       */
      _onDragStart: function (evt) {
        // Create feedback rectangle if it doesn't exist
        if (this._deferredMode && !this._feedbackRect) {
          this._feedbackRect = this._createFeedbackRectangle();
        }
        $super._onDragStart.call(this, evt);
      },
      /**
       * Handler called when splitter is moving on another element
       * @param evt
       * @private
       */
      _onDragOver: function (evt) {
        $super._onDragOver.call(this, evt);
        this._pagePosition = this._isVertical() ? evt.pageY : evt.pageX;
      },
      /**
       * Handler called one time when stopping a drag move on the splitter
       * @param evt
       * @private
       */
      _onDragEnd: function (evt) {
        if (this._deferredMode) {
          this._removeSplitterPosition();
          this._removeFeedbackRectangle();
        }
        $super._onDragEnd.call(this, evt);
      },
      /**
       * Handler called one time when starting a drag move on the splitter using fingers
       * @param evt
       * @private
       */
      _onTouchStart: function (evt) {
        // Create feedback rectangle if it doesn't exist
        if (this._deferredMode && !this._feedbackRect) {
          this._feedbackRect = this._createFeedbackRectangle();
        }
        this._splitStartPos = this._isVertical() ? evt.touches[0].clientY : evt.touches[0].clientX;
        $super._onTouchStart.call(this, evt);
      },
      /**
       * Handler called when moving the splitter using fingers
       * @param evt
       * @private
       */
      _onTouchMove: function (evt) {
        this._pagePosition = this._isVertical() ? evt.touches[0].clientY : evt.touches[0].clientX;
        $super._onTouchMove.call(this, evt);
      },
      /**
       * Handler called one time when stopping a drag move on the splitter using fingers
       * @param evt
       * @private
       */
      _onTouchEnd: function (evt) {
        if (this._deferredMode) {
          this._removeSplitterPosition();
          this._removeFeedbackRectangle();
        }
        $super._onTouchEnd.call(this, evt);
      },
      /**
       * Update splitter x/y splitting coordinates
       * @param evt
       * @private
       */
      _updateResizerDrag: function (evt) {
        this._pagePosition = this._isVertical() ? evt.pageY : evt.pageX;
        this._resizerDragPosition = this._isVertical() ? evt.pageY : evt.pageX;
      },
      /**
       * Create a feedback rectangle for split move
       * @return {HTMLDivElement}
       */
      _createFeedbackRectangle: function () {
        const rect = document.createElement("div");
        this._element.parentNode.appendChild(rect);
        return rect;
      },
      /**
       * Remove from DOM the feedback rectangle and destroy it
       * @private
       */
      _removeFeedbackRectangle: function () {
        if (this._feedbackRect) {
          this._feedbackRect.remove();
          this._feedbackRect = null;
        }
      },
      /**
       * Update feedback rectangle position and size on split move
       * @param {Number} delta - moved distance (px)
       */
      _updateFeedbackRectangle(delta) {
        if (!this._feedbackRect) {
          return;
        }

        const isVertical = this._isVertical();
        const isPositiveDelta = delta > 0;

        // default class
        this._feedbackRect.className = "gbc_SplitterWidget__feedbackRectangle";
        // Define position properties based on orientation
        const position = {
          // Set all properties to their default values
          top: "auto",
          bottom: "auto",
          left: "auto",
          right: "auto",
          width: isVertical ? "100%" : `${Math.abs(delta)}px`,
          height: isVertical ? `${Math.abs(delta)}px` : "100%"
        };

        // Set the appropriate position property based on orientation and delta
        if (isVertical) {
          position[isPositiveDelta ? "top" : "bottom"] = `${this._splitterSize / 2}px`;
          this._feedbackRect.classList.add(isPositiveDelta ? "bottom" : "top");
        } else {
          position[isPositiveDelta ? "left" : "right"] = `${this._splitterSize / 2}px`;
          this._feedbackRect.classList.add(isPositiveDelta ? "right" : "left");
        }

        // Apply all styles at once
        // Take in note that spread syntax approach doesn't work here because "style" object is a CSSStyleDeclaration object with special behavior
        Object.assign(this._feedbackRect.style, position);
      },

      /**
       * Remove absolute CSS position on the splitter
       * @private
       */
      _removeSplitterPosition: function () {
        this.getElement().style.removeProperty(this._isVertical() ? "top" : "left");
      },
      /**
       * Add absolute CSS position on the splitter
       * @param delta
       * @private
       */
      _addSplitterPosition: function (delta) {
        this.getElement().style.setProperty(this._isVertical() ? "top" : "left", `${delta}px`, "important");
      },
      /**
       * Update splitter position horizontally or vertically
       * @param delta
       * @private
       */
      _moveSplitter: function (delta) {
        // Update feedback rectangle
        this._updateFeedbackRectangle(delta);
        // move splitter using CSS absolute position
        this._addSplitterPosition(delta);
      }
    };
  });
  cls.WidgetFactory.registerBuilder("HVBoxSplitter", cls.HVBoxSplitterWidget);
});
